/**
 * 🔄 Qawaem SYNC ENGINE v1.3.4 - SMART SYNC SYSTEM
 * 
 * Handles bidirectional data synchronization between local storage
 * and Cloudflare D1 for ONLINE license mode.
 * 
 * FEATURES v1.3.4:
 * ✅ Smart Batch Push: Groups changes after 5 min inactivity
 * ✅ Smart Pull: Checks timestamp first, pulls only if needed
 * ✅ Pull Check Interval: Every 10 minutes (reduced from 5)
 * ✅ Manual Sync: Force sync button for urgent changes
 * ✅ Offline Support: Auto-sync when reconnected
 * ✅ Countdown UI: Shows time until next sync
 * ✅ Change Counter: Shows pending modifications
 */

import localDbService from './localDbService';
import i18n from '../../i18n';

const sy = (key, opts) => i18n.t(`serviceMessages.sync.${key}`, opts);

const SYNC_API_BASE = 'https://qawaem-platform.qawaemaicode.workers.dev';
const PULL_CHECK_INTERVAL = 10 * 60 * 1000; // 10 minutes - Check timestamp
const PUSH_BATCH_DELAY = 5 * 60 * 1000; // 5 minutes - Group changes after inactivity
const CONFLICT_RESOLUTION_STRATEGY = 'last-write-wins';

class SyncService {
  constructor() {
    // Timers
    this.pullCheckInterval = null;
    this.pushBatchTimer = null;
    
    // State
    this.isSyncing = false;
    this.lastSyncTimestamp = null;
    this.lastServerTimestamp = null;
    this.companyId = null;
    this.deviceId = null;
    this.licenseKey = null;
    this.syncEnabled = false;
    
    // Pending changes (for batch push)
    this.pendingChanges = [];
    this.pendingChangesCount = 0;
    
    // Callbacks
    this.syncListeners = [];
    
    // Countdown (for UI)
    this.nextPushTime = null;
    this.nextPullCheckTime = null;
  }

  /**
   * 🚀 INITIALIZE SYNC ENGINE
   * Called after successful license activation with ONLINE mode
   */
  async initialize(licenseKey, companyId) {
    try {
      console.log(`🔄 Initializing sync engine for company: ${companyId}`);

      this.licenseKey = licenseKey;
      this.companyId = companyId;
      this.deviceId = await this.getOrCreateDeviceId();

      // Load last sync timestamp from local storage
      const storedTimestamp = localStorage.getItem('qawaem_last_sync_timestamp');
      if (storedTimestamp) {
        this.lastSyncTimestamp = storedTimestamp;
      }

      // Register device with cloud
      const registered = await this.registerDevice();
      if (!registered) {
        throw new Error(sy('failed_register_device'));
      }

      // Enable sync
      this.syncEnabled = true;

      // Start sync interval (every 5 minutes)
      this.startSyncInterval();

      // Perform initial sync
      await this.performSync();

      // Set up change tracking
      this.setupChangeTracking();

      console.log(`✅ Sync engine initialized successfully`);
      this.notifyListeners('initialized', { companyId, deviceId: this.deviceId });

      return true;
    } catch (error) {
      console.error('❌ Sync initialization error:', error);
      this.notifyListeners('error', { type: 'initialization', error: error.message });
      return false;
    }
  }

  /**
   * 🆔 GET OR CREATE DEVICE ID
   * Generates a unique, persistent device identifier
   */
  async getOrCreateDeviceId() {
    let deviceId = localStorage.getItem('qawaem_device_id');
    
    if (!deviceId) {
      // Get device ID from main process (more stable, based on hardware)
      if (window.electronAPI?.sync?.getDeviceId) {
        deviceId = await window.electronAPI.sync.getDeviceId();
      } else {
        // Fallback for web mode
        const timestamp = Date.now().toString(36);
        const random = Math.random().toString(36).substring(2, 10);
        const platform = navigator.platform || 'unknown';
        deviceId = `device_${platform.toLowerCase()}_${timestamp}_${random}`;
      }
      
      localStorage.setItem('qawaem_device_id', deviceId);
      console.log(`🆔 Device ID: ${deviceId}`);
    }

    return deviceId;
  }

  /**
   * 📱 REGISTER DEVICE WITH CLOUD
   */
  async registerDevice() {
    try {
      const response = await fetch(`${SYNC_API_BASE}/sync/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_id: this.companyId,
          device_id: this.deviceId,
          license_key: this.licenseKey,
          device_name: localStorage.getItem('qawaem_device_name') || this.deviceId,
          platform: navigator.platform || 'unknown'
        })
      });

      const data = await response.json();
      
      if (data.success) {
        console.log(`📱 Device registered:`, data);
        return true;
      } else {
        console.error('Device registration failed:', data.error);
        return false;
      }
    } catch (error) {
      console.error('Device registration error:', error);
      return false;
    }
  }

  /**
   * ⏰ START SMART SYNC SYSTEM (v1.3.4)
   * - Pull check: Every 10 minutes (timestamp check)
   * - Push batch: 5 minutes after last change
   */
  startSyncInterval() {
    // Clear any existing intervals
    if (this.pullCheckInterval) {
      clearInterval(this.pullCheckInterval);
    }
    if (this.pushBatchTimer) {
      clearTimeout(this.pushBatchTimer);
    }

    console.log(`⏰ Starting Smart Sync System v1.3.4:`);
    console.log(`   📥 PULL Check: Every ${PULL_CHECK_INTERVAL / 60000} minutes`);
    console.log(`   📤 PUSH Batch: ${PUSH_BATCH_DELAY / 60000} min after last change`);

    // Start PULL check interval (every 10 minutes)
    this.pullCheckInterval = setInterval(async () => {
      if (!this.isSyncing && this.syncEnabled) {
        await this.performSmartPull();
      }
    }, PULL_CHECK_INTERVAL);

    // Set next pull check time
    this.nextPullCheckTime = Date.now() + PULL_CHECK_INTERVAL;

    // 🌐 Listen for online/offline events
    this.setupOnlineOfflineListeners();

    // Perform initial pull check
    this.performSmartPull();
  }

  /**
   * 🌐 SETUP ONLINE/OFFLINE DETECTION
   * Automatically sync when coming back online
   */
  setupOnlineOfflineListeners() {
    // Remove existing listeners first
    if (this.onlineListener) {
      window.removeEventListener('online', this.onlineListener);
    }
    if (this.offlineListener) {
      window.removeEventListener('offline', this.offlineListener);
    }

    // Online event: device reconnected to internet
    this.onlineListener = async () => {
      console.log('🌐 Network connection restored - syncing pending changes...');
      this.notifyListeners('network_online', { timestamp: new Date().toISOString() });
      
      // Wait 2 seconds for network to stabilize, then sync
      setTimeout(async () => {
        if (this.syncEnabled && !this.isSyncing) {
          await this.performSync();
        }
      }, 2000);
    };

    // Offline event: device lost internet connection
    this.offlineListener = () => {
      console.log('📴 Network connection lost - sync paused');
      this.notifyListeners('network_offline', { timestamp: new Date().toISOString() });
    };

    window.addEventListener('online', this.onlineListener);
    window.addEventListener('offline', this.offlineListener);

    console.log('🌐 Online/Offline detection enabled');
  }

  /**
   * 🛑 STOP SYNC SYSTEM
   */
  stopSyncInterval() {
    // Stop pull check interval
    if (this.pullCheckInterval) {
      clearInterval(this.pullCheckInterval);
      this.pullCheckInterval = null;
    }

    // Stop push batch timer
    if (this.pushBatchTimer) {
      clearTimeout(this.pushBatchTimer);
      this.pushBatchTimer = null;
    }

    // Remove online/offline listeners
    if (this.onlineListener) {
      window.removeEventListener('online', this.onlineListener);
      this.onlineListener = null;
    }
    if (this.offlineListener) {
      window.removeEventListener('offline', this.offlineListener);
      this.offlineListener = null;
    }

    console.log(`🛑 Smart Sync System stopped`);
  }

  /**
   * 🔄 PERFORM FULL SYNC CYCLE
   * 1. Push local changes
   * 2. Pull remote changes
   */
  async performSync() {
    if (this.isSyncing || !this.syncEnabled) {
      console.log('⏸️ Sync already in progress or disabled');
      return;
    }

    // Check if online before attempting sync
    if (!navigator.onLine) {
      console.log('📴 Offline - skipping sync (will retry when online)');
      return;
    }

    this.isSyncing = true;
    this.notifyListeners('sync_started', { timestamp: new Date().toISOString() });

    try {
      console.log(`🔄 Starting sync cycle...`);

      // STEP 1: Push local changes to cloud
      const pushResult = await this.pushChanges();
      console.log(`📤 Push result:`, pushResult);

      // STEP 2: Pull remote changes from cloud
      const pullResult = await this.pullChanges();
      console.log(`📥 Pull result:`, pullResult);

      // Update last sync timestamp
      this.lastSyncTimestamp = new Date().toISOString();
      localStorage.setItem('qawaem_last_sync_timestamp', this.lastSyncTimestamp);

      console.log(`✅ Sync cycle completed successfully`);
      this.notifyListeners('sync_completed', {
        pushed: pushResult.count || 0,
        pulled: pullResult.count || 0,
        timestamp: this.lastSyncTimestamp
      });

    } catch (error) {
      console.error('❌ Sync cycle error:', error);
      this.notifyListeners('sync_error', { error: error.message });
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * 📤 PUSH LOCAL CHANGES TO CLOUD
   */
  async pushChanges() {
    try {
      // Get pending changes from local change log
      const changes = await this.getPendingChanges();
      
      if (changes.length === 0) {
        console.log('📤 No local changes to push');
        return { count: 0 };
      }

      console.log(`📤 Pushing ${changes.length} local changes to cloud...`);

      const response = await fetch(`${SYNC_API_BASE}/sync/push`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_id: this.companyId,
          device_id: this.deviceId,
          license_key: this.licenseKey,
          changes: changes
        })
      });

      const data = await response.json();
      
      if (data.success) {
        // Clear pushed changes from local log
        await this.clearPushedChanges(changes);
        return { count: data.synced_count };
      } else {
        throw new Error(data.error || sy('failed_push_changes'));
      }
    } catch (error) {
      console.error('Push changes error:', error);
      throw error;
    }
  }

  /**
   * 📥 PULL REMOTE CHANGES FROM CLOUD
   */
  async pullChanges() {
    try {
      console.log(`📥 Pulling remote changes from cloud...`);

      const params = new URLSearchParams({
        company_id: this.companyId,
        device_id: this.deviceId,
        license_key: this.licenseKey
      });

      if (this.lastSyncTimestamp) {
        params.append('last_synced_timestamp', this.lastSyncTimestamp);
      }

      const response = await fetch(`${SYNC_API_BASE}/sync/pull?${params}`);
      const data = await response.json();
      
      if (data.success) {
        const changes = data.changes || [];
        console.log(`📥 Received ${changes.length} remote changes`);

        if (changes.length > 0) {
          // Apply changes to local database
          await this.applyRemoteChanges(changes);
        }

        return { count: changes.length };
      } else {
        throw new Error(data.error || sy('failed_pull_changes'));
      }
    } catch (error) {
      console.error('Pull changes error:', error);
      throw error;
    }
  }

  /**
   * 📝 LOG LOCAL CHANGE FOR SYNC
   * Called when local data is modified
   */
  async logChange(tableName, action, recordId, payload = null) {
    // ✅ NOTE: Change logging is now automatic via IPC handlers in main-standalone.js
    // All api-post, api-put, api-delete operations automatically call logChange() in main process
    // This method is kept for backward compatibility but does nothing
    return;
  }

  /**
   * 📋 GET PENDING CHANGES TO PUSH
   */
  async getPendingChanges() {
    try {
      // Use IPC to get pending changes from main process
      const pendingChanges = await window.electronAPI.sync.getPending();

      return pendingChanges.slice(0, 100).map(change => ({
        id: change.id,
        record_id: change.record_id,
        table_name: change.table_name,
        action: change.action,
        payload: change.payload ? JSON.parse(change.payload) : null,
        timestamp: change.timestamp,
        device_id: change.device_id
      }));
    } catch (error) {
      console.error('Get pending changes error:', error);
      return [];
    }
  }

  /**
   * ✅ CLEAR PUSHED CHANGES FROM LOCAL LOG
   */
  async clearPushedChanges(changes) {
    try {
      // Extract change IDs
      const changeIds = changes.map(c => c.id).filter(id => id);
      
      if (changeIds.length === 0) return;
      
      // Use IPC to mark changes as synced
      await window.electronAPI.sync.markSynced(changeIds);

      console.log(`✅ Marked ${changeIds.length} changes as synced`);
    } catch (error) {
      console.error('Clear pushed changes error:', error);
    }
  }

  /**
   * 🔧 APPLY REMOTE CHANGES TO LOCAL DATABASE
   */
  async applyRemoteChanges(changes) {
    try {
      console.log(`🔧 Applying ${changes.length} remote changes to local database...`);

      // Use IPC to apply changes in main process
      const result = await window.electronAPI.sync.applyChanges(changes);

      if (result.success) {
        console.log(`✅ Applied ${result.applied} remote changes`);
        this.notifyListeners('changes_applied', { count: result.applied });
      } else {
        throw new Error(result.error || sy('failed_apply_changes'));
      }
    } catch (error) {
      console.error('Apply remote changes error:', error);
      throw error;
    }
  }

  // ✅ NOTE: applyCreate, applyUpdate, applyDelete, detectConflict, resolveConflict removed
  // All change application is now handled by main process via IPC
  // This simplifies the code and ensures data consistency
  // Conflict resolution uses last-write-wins strategy in main process

  /**
   * ➕ APPLY CREATE OPERATION (DEPRECATED - now handled by IPC)
   */
  async applyCreate(change) {
    const { table_name, payload } = change;
    
    if (!payload) {
      console.warn('Create operation has no payload:', change);
      return;
    }

    // Build INSERT query
    const columns = Object.keys(payload);
    const values = Object.values(payload);
    const placeholders = columns.map(() => '?').join(',');
    
    const query = `INSERT OR REPLACE INTO ${table_name} (${columns.join(',')}) VALUES (${placeholders})`;
    
    await localDbService.execute(query, values);
    console.log(`➕ Created ${table_name} record:`, change.record_id);
  }

  /**
   * ✏️ APPLY UPDATE OPERATION
   */
  async applyUpdate(change) {
    const { table_name, payload, record_id } = change;
    
    if (!payload) {
      console.warn('Update operation has no payload:', change);
      return;
    }

    // Check for conflicts (last-write-wins strategy)
    const conflict = await this.detectConflict(change);
    if (conflict) {
      await this.resolveConflict(change, conflict);
      return;
    }

    // Build UPDATE query
    const columns = Object.keys(payload);
    const values = Object.values(payload);
    const setClause = columns.map(col => `${col} = ?`).join(',');
    
    const query = `UPDATE ${table_name} SET ${setClause} WHERE id = ?`;
    
    await localDbService.execute(query, [...values, record_id]);
    console.log(`✏️ Updated ${table_name} record:`, record_id);
  }

  /**
   * 🗑️ APPLY DELETE OPERATION
   */
  async applyDelete(change) {
    const { table_name, record_id } = change;
    
    await localDbService.execute(
      `DELETE FROM ${table_name} WHERE id = ?`,
      [record_id]
    );
    
    console.log(`🗑️ Deleted ${table_name} record:`, record_id);
  }

  /**
   * 🔍 DETECT CONFLICT
   * Checks if local record was modified more recently than remote change
   */
  async detectConflict(change) {
    try {
      const { table_name, record_id, timestamp } = change;
      
      // Get local record
      const localRecord = await localDbService.execute(
        `SELECT * FROM ${table_name} WHERE id = ?`,
        [record_id]
      );

      if (localRecord.length === 0) {
        return null; // No conflict if record doesn't exist locally
      }

      const local = localRecord[0];
      const localTimestamp = local.updated_at || local.created_at;
      
      if (!localTimestamp) {
        return null; // No timestamp to compare
      }

      // Compare timestamps
      const remoteDate = new Date(timestamp);
      const localDate = new Date(localTimestamp);

      if (localDate > remoteDate) {
        // Local is newer - potential conflict
        return {
          local: local,
          remote: change,
          localTimestamp: localDate,
          remoteTimestamp: remoteDate
        };
      }

      return null; // No conflict
    } catch (error) {
      console.error('Conflict detection error:', error);
      return null;
    }
  }

  /**
   * 🔧 RESOLVE CONFLICT
   * Uses last-write-wins strategy by default
   */
  async resolveConflict(remoteChange, conflict) {
    console.warn('🔧 Conflict detected:', conflict);

    if (CONFLICT_RESOLUTION_STRATEGY === 'last-write-wins') {
      // Remote is older, keep local version
      console.log('✅ Keeping local version (newer timestamp)');
      this.notifyListeners('conflict_resolved', {
        strategy: 'last-write-wins',
        winner: 'local',
        record_id: remoteChange.record_id
      });
    } else if (CONFLICT_RESOLUTION_STRATEGY === 'manual') {
      // Notify UI to show conflict resolution dialog
      this.notifyListeners('conflict_detected', {
        local: conflict.local,
        remote: remoteChange,
        record_id: remoteChange.record_id
      });
    }
  }

  /**
   * 🎧 SET UP CHANGE TRACKING
   * Monitors database operations to automatically log changes
   */
  setupChangeTracking() {
    // This would hook into the localDbService execute method
    // For now, changes must be logged manually using logChange()
    console.log('📊 Change tracking ready (manual logging mode)');
  }

  /**
   * 📥 PERFORM SMART PULL (v1.3.4)
   * Check server timestamp first, only pull if changes detected
   */
  async performSmartPull() {
    if (!navigator.onLine) {
      console.log('📴 Offline - skipping pull check');
      return;
    }

    try {
      // Update next pull check time
      this.nextPullCheckTime = Date.now() + PULL_CHECK_INTERVAL;
      this.notifyListeners('pull_check_started', { timestamp: new Date().toISOString() });

      // 1. Check server timestamp (lightweight request)
      const timestampCheck = await this.checkServerTimestamp();
      
      if (timestampCheck.hasNewChanges) {
        // 2. New changes detected - perform full PULL
        console.log(`📥 New changes detected! Last server: ${timestampCheck.serverTimestamp}`);
        await this.pullChanges();
        this.lastServerTimestamp = timestampCheck.serverTimestamp;
        localStorage.setItem('qawaem_last_server_timestamp', timestampCheck.serverTimestamp);
      } else {
        // 3. No new changes - skip pull (save bandwidth!)
        console.log(`⏸️ No new changes (server: ${timestampCheck.serverTimestamp})`);
      }

      this.notifyListeners('pull_check_completed', { 
        hasNewChanges: timestampCheck.hasNewChanges,
        timestamp: new Date().toISOString() 
      });

    } catch (error) {
      console.error('❌ Smart pull error:', error);
      this.notifyListeners('pull_check_error', { error: error.message });
    }
  }

  /**
   * ⏰ CHECK SERVER TIMESTAMP
   * Lightweight request to check if there are new changes
   */
  async checkServerTimestamp() {
    const params = new URLSearchParams({
      company_id: this.companyId,
      device_id: this.deviceId,
      license_key: this.licenseKey
    });

    const response = await fetch(`${SYNC_API_BASE}/sync/timestamp?${params}`);
    
    if (!response.ok) {
      throw new Error(sy('server_returned', { status: response.status, statusText: response.statusText }));
    }
    
    const data = await response.json();

    if (!data || !data.success) {
      throw new Error(data?.error || sy('failed_check_timestamp'));
    }

    const serverTimestamp = data.last_timestamp;
    const storedTimestamp = this.lastServerTimestamp || localStorage.getItem('qawaem_last_server_timestamp');

    return {
      serverTimestamp: serverTimestamp,
      hasNewChanges: serverTimestamp && serverTimestamp !== storedTimestamp,
      pendingChanges: data.pending_changes || 0
    };
  }

  /**
   * 📤 SCHEDULE PUSH BATCH (v1.3.4)
   * Called when local change detected
   * Groups changes and pushes after 5 min of inactivity
   */
  schedulePushBatch() {
    // Clear existing timer
    if (this.pushBatchTimer) {
      clearTimeout(this.pushBatchTimer);
    }

    // Update next push time
    this.nextPushTime = Date.now() + PUSH_BATCH_DELAY;

    // Set new timer (5 minutes)
    this.pushBatchTimer = setTimeout(async () => {
      await this.performBatchPush();
    }, PUSH_BATCH_DELAY);

    console.log(`📤 Push batch scheduled in ${PUSH_BATCH_DELAY / 60000} minutes`);
    this.notifyListeners('push_scheduled', { 
      nextPushTime: this.nextPushTime,
      pendingCount: this.pendingChangesCount 
    });
  }

  /**
   * 📤 PERFORM BATCH PUSH
   * Send all pending changes grouped together
   */
  async performBatchPush() {
    if (!navigator.onLine) {
      console.log('📴 Offline - push will retry when online');
      return;
    }

    if (this.isSyncing) {
      console.log('⏸️ Sync already in progress - will retry');
      return;
    }

    this.isSyncing = true;
    this.notifyListeners('push_started', { timestamp: new Date().toISOString() });

    try {
      const result = await this.pushChanges();
      
      // Clear push timer
      this.pushBatchTimer = null;
      this.nextPushTime = null;

      console.log(`✅ Batch push completed: ${result.count} changes sent`);
      this.notifyListeners('push_completed', { 
        count: result.count,
        timestamp: new Date().toISOString() 
      });

    } catch (error) {
      console.error('❌ Batch push error:', error);
      this.notifyListeners('push_error', { error: error.message });
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * ⚡ FORCE SYNC NOW (v1.3.4)
   * Manual sync button - immediate push and pull
   */
  async forceSyncNow() {
    if (this.isSyncing) {
      console.log('⏸️ Sync already in progress');
      return { success: false, message: sy('already_in_progress') };
    }

    if (!navigator.onLine) {
      return { success: false, message: sy('no_internet') };
    }

    console.log('⚡ FORCE SYNC NOW - Manual trigger');
    this.notifyListeners('force_sync_started', { timestamp: new Date().toISOString() });

    try {
      // Cancel pending timers
      if (this.pushBatchTimer) {
        clearTimeout(this.pushBatchTimer);
        this.pushBatchTimer = null;
      }

      // Perform full sync immediately
      await this.performSync();

      return { success: true, message: sy('completed') };

    } catch (error) {
      console.error('❌ Force sync error:', error);
      return { success: false, message: error.message };
    }
  }

  /**
   * 🔔 ON LOCAL CHANGE DETECTED (v1.3.5 - Auto Critical Detection)
   * Called by main process when a local change is made
   * Detects critical changes and syncs immediately
   */
  onLocalChangeDetected(changeData) {
    this.pendingChangesCount++;
    
    // Check if change is critical
    const isCritical = this.isCriticalChange(changeData);
    
    if (isCritical) {
      console.log('⚠️ Critical change detected - forcing immediate sync:', changeData);
      // Force sync after 30 seconds (allow grouping nearby changes)
      setTimeout(() => {
        this.forceSyncNow();
      }, 30000); // 30 seconds delay
      
      this.notifyListeners('critical_change_detected', {
        changeData: changeData,
        syncIn: 30 // seconds
      });
    } else {
      // Normal change - batch after 5 min
      this.schedulePushBatch();
    }
    
    this.notifyListeners('local_change_detected', {
      pendingCount: this.pendingChangesCount,
      nextPushTime: this.nextPushTime,
      isCritical: isCritical
    });
  }

  /**
   * 🚨 IS CRITICAL CHANGE? (v1.3.5)
   * Determines if a change requires immediate sync
   */
  isCriticalChange(changeData) {
    if (!changeData) return false;

    const { table_name, action, payload } = changeData;

    // Critical tables (any change)
    const CRITICAL_TABLES = ['settings', 'categories'];
    if (CRITICAL_TABLES.includes(table_name)) {
      return true;
    }

    // Critical actions (any table)
    const CRITICAL_ACTIONS = ['delete'];
    if (CRITICAL_ACTIONS.includes(action)) {
      return true;
    }

    // Products: Price changes are critical
    if (table_name === 'products' && action === 'update' && payload) {
      try {
        const data = typeof payload === 'string' ? JSON.parse(payload) : payload;
        if (data.sellingPrice || data.purchasePrice || data.price) {
          return true;
        }
      } catch (e) {
        // If can't parse, treat as non-critical
      }
    }

    // Clients/Suppliers: Deletions are critical
    if (['clients', 'suppliers', 'partners'].includes(table_name) && action === 'delete') {
      return true;
    }

    return false;
  }

  /**
   * 📊 GET SYNC STATUS (v1.3.5 - Offline Safe)
   */
  async getSyncStatus() {
    // Always return local status (no HTTP required)
    const localStatus = {
      enabled: this.syncEnabled,
      syncing: this.isSyncing,
      lastSync: this.lastSyncTimestamp,
      pendingChanges: this.pendingChangesCount || 0,
      companyId: this.companyId,
      deviceId: this.deviceId,
      licenseKey: this.licenseKey,
      nextPushTime: this.nextPushTime,
      nextPullCheckTime: this.nextPullCheckTime,
      offline: !navigator.onLine
    };

    // If offline, return local status only
    if (!navigator.onLine) {
      return localStatus;
    }

    // If no credentials, return local status
    if (!this.companyId || !this.deviceId || !this.licenseKey) {
      return localStatus;
    }

    // If online, try to get server status (optional, non-blocking)
    try {
      const params = new URLSearchParams({
        company_id: this.companyId,
        device_id: this.deviceId,
        license_key: this.licenseKey
      });

      const response = await fetch(`${SYNC_API_BASE}/sync/status?${params}`, {
        signal: AbortSignal.timeout(5000) // 5 second timeout
      });
      const data = await response.json();
      
      if (data.success) {
        return {
          ...localStatus,
          pendingChanges: data.pending_changes || localStatus.pendingChanges,
          totalDevices: data.total_devices || 1
        };
      }
    } catch (error) {
      // Silently fail - return local status
      console.log('⚠️ Could not fetch server status (using local):', error.message);
    }

    return localStatus;
  }

  /**
   * 🎯 MANUAL SYNC TRIGGER
   * Allows user to manually trigger a sync
   */
  async triggerManualSync() {
    if (this.isSyncing) {
      console.log('⏸️ Sync already in progress');
      return false;
    }

    console.log('🎯 Manual sync triggered');
    await this.performSync();
    return true;
  }

  /**
   * 🎧 ADD SYNC EVENT LISTENER
   */
  addListener(callback) {
    this.syncListeners.push(callback);
  }

  /**
   * 🔕 REMOVE SYNC EVENT LISTENER
   */
  removeListener(callback) {
    this.syncListeners = this.syncListeners.filter(cb => cb !== callback);
  }

  /**
   * 📢 NOTIFY ALL LISTENERS
   */
  notifyListeners(event, data) {
    this.syncListeners.forEach(callback => {
      try {
        callback(event, data);
      } catch (error) {
        console.error('Listener notification error:', error);
      }
    });
  }

  /**
   * 🛑 STOP SYNC ENGINE
   */
  stop() {
    console.log('🛑 Stopping sync engine...');
    this.stopSyncInterval();
    this.syncEnabled = false;
    this.notifyListeners('stopped', {});
  }
}

// Create singleton instance
const syncService = new SyncService();

export default syncService;
export { syncService };


